import {Injectable} from '@angular/core';
import {PushEventService} from '@app/services/push-event/push-event.service';
import {Absent, Available} from '@app/services/push-event/push-events';
import {checkResponse} from '@app/shared/utils/app-utils';
import {AbsenceEntry, AbsenceReason, AbsenceService, AbsenceV2Service} from '@gt-api';
import {TranslateService} from '@ngx-translate/core';
import {BehaviorSubject, forkJoin, Observable, of, ReplaySubject} from 'rxjs';
import {catchError, concatMap, filter, first, map, mapTo, switchMap, tap} from 'rxjs/operators';
import {GtAuthenticationService} from '@app/services/gt-authentication.service';

@Injectable({
  providedIn: 'root'
})
export class GtAbsenceService {
  private absencesStore = new Map<string, ReplaySubject<AbsenceEntry[]>>();
  private absenceReasonsStore = new BehaviorSubject<Map<string, AbsenceReason[]>>(new Map<string, AbsenceReason[]>());

  absenceReasons$ = (login: string) => this.absenceReasonsStore.pipe(
    map(reasons => reasons.get(login)),
    filter(reasons => !!reasons),
  );

  constructor(
    private authenticationService: GtAuthenticationService,
    private absenceService: AbsenceService,
    private absenceV2Service: AbsenceV2Service,
    private pushEventService: PushEventService,
    private translateService: TranslateService
  ) {
    this.pushEventService.pushSubjects.Available.pipe(concatMap(e => this.updateAbsence(e))).subscribe();
    this.pushEventService.pushSubjects.Absent.pipe(concatMap(e => this.updateAbsence(e))).subscribe();
    this.translateService.onLangChange.pipe(
      switchMap(() => this.fetchAbsenceReasons(this.authenticationService.oname, this.authenticationService.login)),
    ).subscribe();
  }

  private updateAbsence(absenceEvent: Available | Absent): Observable<void> {
    if (this.absencesStore.has(absenceEvent.oname)) {
      // Only update if the absences are cached already.
      // Since we cannot be sure which absence entries are left in DB when we get a push, we fetch absences for the login.
      return this.fetchAbsencesForLogin(absenceEvent.oname, absenceEvent.login).pipe(
        switchMap(absences =>
          // get existing absences and update with values for single login
          this.getAbsences(absenceEvent.oname, false).pipe(
            map(existingAbsences => [
              ...existingAbsences.filter(a => !(a.organization === absenceEvent.oname && a.login === absenceEvent.login)),
              ...absences
            ])
          )
        ),
        tap(absences => {
          this.absencesStore.get(absenceEvent.oname).next(absences);
        }),
        catchError(err => {
          // do not let the subscription die
          console.log('Error fetching absence entries for user', err);
          return of();
        }),
        mapTo(void 0)
      );
    } else {
      return of();
    }
  }

  getAbsenceReasons(oname: string, login: string): Observable<AbsenceReason[]> {
    if (!this.absenceReasonsStore.value.has(login)) {
      this.absenceReasonsStore.value.set(login, null);
      this.fetchAbsenceReasons(oname, login).subscribe();
    }
    return this.absenceReasons$(login).pipe(first());
  }

  // Fetch absenceReasons for a given user from the backend
  private fetchAbsenceReasons(oname: string, login: string): Observable<Array<AbsenceReason>> {
    return this.absenceV2Service.getAbsenceReasonsForUser1(oname, login, this.translateService.currentLang).pipe(
      checkResponse(),
      map(r => r.absenceReasons),
      tap(ar => {
        const current = this.absenceReasonsStore.value;
        current.set(login, ar);
        this.absenceReasonsStore.next(new Map(current));
      })
    );
  }

  // Get absenceEntries for a given user. Will try to read from cache.
  // If cache is not available for the user's organization, it will be fetched from the backend.
  getAbsenceEntriesForUser(oname: string, login: string): Observable<Array<AbsenceEntry>> {
    return this.getAbsences(oname, false).pipe(map(entries => entries.filter(e => e.login === login)));
  }

  // Delete absenceEntry by ID. Will trigger push message from backend
  deleteAbsenceEntry(id: number): Observable<void> {
    return this.absenceService.removeAbsenceEntry1('', '', id).pipe(checkResponse(), mapTo(void 0));
  }

  // Add an absenceEntry to the DB. Will trigger push message from backend
  addAbsenceEntry(entry: AbsenceEntry): Observable<number> {
    delete entry.active;

    const addAbsenceEntry$ = this.absenceService.addAbsenceEntry1(entry).pipe(
      checkResponse(),
      map(e => e.absenceEntry)
    );

    return forkJoin([addAbsenceEntry$, this.getAbsences(entry.organization, false)]).pipe(
      tap(r => {
        const [addedEntry, existingEntries] = r;
        this.absencesStore.get(addedEntry.organization).next([...existingEntries, addedEntry]);
      }),
      map(r => r[0].id)
    );
  }

  editAbsenceEntry(entry: AbsenceEntry): Observable<AbsenceEntry> {
    return this.absenceService.editAbsenceEntry1(entry).pipe(
      checkResponse(),
      map(r => r.absenceEntry)
    );
  }

  // Update the given absenceReason
  setAbsenceReasonForUser(oname: string, login: string, reason: AbsenceReason): Observable<void> {
    return this.absenceV2Service.setAbsenceReasonForUser1(oname, login, true, reason).pipe(checkResponse(), mapTo(void 0));
  }

  // Delete all active absenceEntries for a given user
  removeCurrentAbsentEntries(oname: string, login: string): Observable<void> {
    return this.absenceService.removeCurrentAbsenceEntries1(oname, login).pipe(checkResponse(), mapTo(void 0));
  }

  // Get reference to the observable wrapping the absenceEntries of a given oname. Use this to be notified of changes in the absence list.
  getAbsences(oname: string, subscribe: boolean): Observable<AbsenceEntry[]> {
    if (!this.absencesStore.has(oname)) {
      this.absencesStore.set(oname, new ReplaySubject<AbsenceEntry[]>(1));
      // Do initial fetch
      this.fetchAbsencesForOname(oname).subscribe();
    }

    if (subscribe) {
      return this.absencesStore.get(oname);
    } else {
      return this.absencesStore.get(oname).pipe(first());
    }
  }

  getAbsenceEntryById(absenceId: number, oname: string): Observable<AbsenceEntry> {
    if (absenceId !== 0) {
      return this.getAbsences(oname, false).pipe(
        switchMap(entries => {
          const existingEntry = entries.find(e => e.id === absenceId);
          if (!existingEntry) {
            return this.fetchAbsencesForOname(oname).pipe(map(newEntries => newEntries.find(e => e.id === absenceId)));
          } else {
            return of(existingEntry);
          }
        }),
        catchError(err => {
          console.log('Error getting absence entry by id', err);
          return of(null);
        })
      );
    } else {
      return of(null);
    }
  }

  // Fetch absenceEntries for a given org from the backend
  private fetchAbsencesForOname(oname: string): Observable<AbsenceEntry[]> {
    // TODO: limit the from/to interval?
    return this.absenceV2Service.getAbsenceEntries2(oname).pipe(
      checkResponse(),
      map(r => r.absenceEntries),
      map(absenceEntries => absenceEntries.map(abs => (abs.reasonId === 101 ? {...abs, endTime: null} : abs))),
      tap(entries => {
        if (this.absencesStore.has(oname)) {
          this.absencesStore.get(oname).next(entries);
        }
      })
    );
  }

  // Fetch absenceEntries for a given login from the backend
  private fetchAbsencesForLogin(oname: string, login: string): Observable<AbsenceEntry[]> {
    return this.absenceV2Service.getAbsenceEntries2(oname, login).pipe(
      checkResponse(),
      map(r => r.absenceEntries),
      map(absenceEntries => absenceEntries.map(abs => (abs.reasonId === 101 ? {...abs, endTime: null} : abs)))
    );
  }
}
